import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Plus, Search, Trash2, Pencil, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

export function Transactions() {
    const [transactions, setTransactions] = useState({ data: [], links: [] });
    const [accounts, setAccounts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ search: '', type: '', account_id: '', category_id: '' });
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [form, setForm] = useState({
        account_id: '',
        transfer_to_account_id: '',
        category_id: '',
        type: 'expense',
        amount: '',
        description: '',
        transaction_date: new Date().toISOString().split('T')[0],
        notes: '',
    });

    const fetchData = () => {
        setLoading(true);
        Promise.all([
            api.getTransactions(filters),
            api.getAccounts(),
            api.getCategories(),
        ])
            .then(([tx, acc, cat]) => {
                setTransactions(tx);
                setAccounts(acc);
                setCategories(cat);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const timeout = setTimeout(fetchData, 300);
        return () => clearTimeout(timeout);
    }, [filters]);

    const resetForm = () => {
        setForm({
            account_id: accounts[0]?.id || '',
            transfer_to_account_id: '',
            category_id: '',
            type: 'expense',
            amount: '',
            description: '',
            transaction_date: new Date().toISOString().split('T')[0],
            notes: '',
        });
        setEditing(null);
        setFormErrors({});
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormErrors({});

        const errors = {};
        if (!form.account_id) errors.account_id = 'Seleziona un conto.';
        if (form.type === 'transfer') {
            if (!form.transfer_to_account_id) errors.transfer_to_account_id = 'Seleziona un conto di destinazione.';
            else if (String(form.transfer_to_account_id) === String(form.account_id)) {
                errors.transfer_to_account_id = 'Il conto di destinazione deve essere diverso da quello di origine.';
            }
        }
        if (form.type !== 'transfer' && !form.category_id) errors.category_id = 'Seleziona una categoria.';
        if (!form.amount || parseFloat(form.amount) <= 0) errors.amount = 'Inserisci un importo maggiore di 0.';
        if (!form.description.trim()) errors.description = 'Inserisci una descrizione.';
        if (!form.transaction_date) errors.transaction_date = 'Seleziona una data.';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const payload = {
            account_id: form.account_id,
            type: form.type,
            amount: parseFloat(form.amount),
            description: form.description.trim(),
            transaction_date: form.transaction_date,
            notes: form.notes.trim(),
        };

        if (form.type === 'transfer') {
            payload.transfer_to_account_id = form.transfer_to_account_id;
        } else {
            payload.category_id = form.category_id;
        }

        const promise = editing
            ? api.updateTransaction(editing.id, payload)
            : api.createTransaction(payload);

        promise
            .then(() => {
                resetForm();
                setShowForm(false);
                fetchData();
            })
            .catch((err) => {
                const backendErrors = err.response?.data?.errors;
                if (backendErrors) {
                    setFormErrors(Object.fromEntries(
                        Object.entries(backendErrors).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : v])
                    ));
                } else {
                    setFormErrors({ generic: err.response?.data?.message || 'Errore durante il salvataggio.' });
                }
            });
    };

    const handleEdit = (t) => {
        setEditing(t);
        setForm({
            account_id: t.account_id,
            transfer_to_account_id: t.transfer_to_account_id || '',
            category_id: t.category_id || '',
            type: t.type,
            amount: t.amount,
            description: t.description,
            transaction_date: t.transaction_date,
            notes: t.notes || '',
        });
        setShowForm(true);
    };

    const handleDelete = (id) => {
        if (confirm('Eliminare questo movimento?')) {
            api.deleteTransaction(id).then(fetchData);
        }
    };

    const isTransfer = form.type === 'transfer';

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Movimenti</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowForm(!showForm);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition"
                >
                    <Plus size={16} />
                    Nuovo movimento
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {formErrors.generic && (
                        <div className="md:col-span-3 p-3 text-sm text-red-700 bg-red-50 rounded-lg">{formErrors.generic}</div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {isTransfer ? 'Conto di origine' : 'Conto'}
                        </label>
                        <select
                            value={form.account_id}
                            onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            required
                        >
                            <option value="">Seleziona</option>
                            {accounts.map((a) => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                        </select>
                        {formErrors.account_id && <p className="text-xs text-red-600 mt-1">{formErrors.account_id}</p>}
                    </div>
                    {isTransfer ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Conto di destinazione</label>
                            <select
                                value={form.transfer_to_account_id}
                                onChange={(e) => setForm({ ...form, transfer_to_account_id: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                                required
                            >
                                <option value="">Seleziona</option>
                                {accounts
                                    .filter((a) => String(a.id) !== String(form.account_id))
                                    .map((a) => (
                                        <option key={a.id} value={a.id}>{a.name}</option>
                                    ))}
                            </select>
                            {formErrors.transfer_to_account_id && <p className="text-xs text-red-600 mt-1">{formErrors.transfer_to_account_id}</p>}
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                            <select
                                value={form.category_id}
                                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            >
                                <option value="">Nessuna</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                            {formErrors.category_id && <p className="text-xs text-red-600 mt-1">{formErrors.category_id}</p>}
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                        <select
                            value={form.type}
                            onChange={(e) => setForm({ ...form, type: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                        >
                            <option value="expense">Spesa</option>
                            <option value="income">Entrata</option>
                            <option value="transfer">Trasferimento</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Importo</label>
                        <input
                            type="number"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            required
                        />
                        {formErrors.amount && <p className="text-xs text-red-600 mt-1">{formErrors.amount}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                        <input
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            required
                        />
                        {formErrors.description && <p className="text-xs text-red-600 mt-1">{formErrors.description}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                        <input
                            type="date"
                            value={form.transaction_date}
                            onChange={(e) => setForm({ ...form, transaction_date: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            required
                        />
                        {formErrors.transaction_date && <p className="text-xs text-red-600 mt-1">{formErrors.transaction_date}</p>}
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                        <input
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                        />
                    </div>
                    <div className="flex items-end">
                        <button type="submit" className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition">
                            {editing ? 'Aggiorna' : 'Salva'}
                        </button>
                    </div>
                </form>
            )}

            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm mb-6 flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                    <input
                        placeholder="Cerca..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none text-sm"
                    />
                </div>
                <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none text-sm"
                >
                    <option value="">Tutti i tipi</option>
                    <option value="expense">Spesa</option>
                    <option value="income">Entrata</option>
                    <option value="transfer">Trasferimento</option>
                </select>
                <select
                    value={filters.account_id}
                    onChange={(e) => setFilters({ ...filters, account_id: e.target.value })}
                    className="px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none text-sm"
                >
                    <option value="">Tutti i conti</option>
                    {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                </select>
                <select
                    value={filters.category_id}
                    onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
                    className="px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none text-sm"
                >
                    <option value="">Tutte le categorie</option>
                    {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <p className="text-gray-500">Caricamento...</p>
            ) : (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="text-left px-6 py-3 font-medium text-gray-500">Data</th>
                                <th className="text-left px-6 py-3 font-medium text-gray-500">Descrizione</th>
                                <th className="text-left px-6 py-3 font-medium text-gray-500">Categoria / Conti</th>
                                <th className="text-right px-6 py-3 font-medium text-gray-500">Importo</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {transactions.data.map((t) => (
                                <tr key={t.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-3 text-gray-500">{format(new Date(t.transaction_date), 'dd MMM yyyy', { locale: it })}</td>
                                    <td className="px-6 py-3 font-medium text-gray-900">{t.description}</td>
                                    <td className="px-6 py-3">
                                        {t.type === 'transfer' ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                                                <span className="font-medium">{t.account?.name}</span>
                                                <ArrowRight size={14} className="text-gray-400" />
                                                <span className="font-medium">{t.transfer_to_account?.name}</span>
                                            </span>
                                        ) : t.category ? (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${t.category.color}20`, color: t.category.color }}>
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.category.color }} />
                                                {t.category.name}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className={`px-6 py-3 text-right font-semibold ${t.type === 'income' ? 'text-emerald-600' : t.type === 'expense' ? 'text-rose-600' : 'text-indigo-600'}`}>
                                        {t.type === 'income' && '+ '}
                                        {t.type === 'expense' && '- '}
                                        {t.type === 'transfer' && '↔ '}
                                        € {parseFloat(t.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-6 py-3">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => handleEdit(t)} className="p-1.5 text-gray-400 hover:text-indigo-600 transition">
                                                <Pencil size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(t.id)} className="p-1.5 text-gray-400 hover:text-rose-600 transition">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
